import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import {
  extractVideoMetadata,
  validateVideo,
  VIDEO_CONSTRAINTS,
} from '../utils/videoMetadata'
import { invalidateSubscriptionCache } from './useSubscription'

// Parallelization settings
const MAX_CONCURRENT_UPLOADS = 3 // Fewer concurrent uploads for videos (larger files)

export function useVideoUpload() {
  const { t } = useTranslation(['errors', 'common'])
  const [uploadQueue, setUploadQueue] = useState([])
  const uploadControllers = useRef(new Map())

  // Create preview URL for video
  const createPreview = (file) => {
    try {
      return URL.createObjectURL(file)
    } catch (error) {
      console.error('Failed to create preview:', error)
      return null
    }
  }

  // Upload a single video file
  const uploadFile = useCallback(async (queueItem, accountId) => {
    const { id, file } = queueItem

    try {
      // Create abort controller for this upload
      const controller = new AbortController()
      uploadControllers.current.set(id, controller)

      // Update status to 'extracting' (metadata extraction)
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, status: 'extracting', progress: 0, error: null }
            : item
        )
      )

      // Extract video metadata
      let metadata
      try {
        metadata = await extractVideoMetadata(file)
      } catch (error) {
        throw new Error(`Failed to read video metadata: ${error.message}`)
      }

      // Validate that metadata was successfully extracted
      if (!metadata) {
        throw new Error('Failed to extract video metadata: metadata is empty')
      }

      // Validate video with metadata
      const validation = validateVideo(file, metadata)
      if (!validation.isValid) {
        // Use i18n key if available, fallback to message for non-i18n contexts
        let errorMessage = validation.error.message
        if (validation.error.key) {
          // Extract the key without 'errors.' prefix since we loaded the 'errors' namespace
          const keyPath = validation.error.key.replace('errors.', '')
          try {
            errorMessage = t(keyPath, validation.error.params || {})
          } catch (e) {
            // Fallback to default message if translation fails
            console.warn(`Failed to translate error key: ${keyPath}`, e)
            errorMessage = validation.error.message
          }
        }
        throw new Error(errorMessage)
      }

      // Update with metadata
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                metadata,
                status: 'uploading',
                progress: 0,
              }
            : item
        )
      )

      // Create FormData with video + metadata
      const formData = new FormData()
      formData.append('file', file)
      formData.append('instagram_account_id', accountId)

      // Add video metadata
      if (metadata.duration !== undefined) {
        formData.append('video_duration', metadata.duration.toString())
      }
      if (metadata.resolution) {
        formData.append('video_resolution', metadata.resolution)
      }
      if (metadata.codec) {
        formData.append('video_codec', metadata.codec)
      }
      if (metadata.fps !== undefined) {
        formData.append('video_fps', metadata.fps.toString())
      }
      if (metadata.hasAudio !== undefined) {
        formData.append('video_has_audio', metadata.hasAudio.toString())
      }

      // Upload with progress tracking
      const response = await api.uploadMedia(formData, {
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          setUploadQueue((prev) =>
            prev.map((item) => (item.id === id ? { ...item, progress } : item))
          )
        },
      })

      // Update status to complete (NO processing step for videos)
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'complete',
                progress: 100,
                mediaId: response.media_id,
                videoData: {
                  media_id: response.media_id,
                  media_type: response.media_type,
                  blob_path: response.blob_path,
                  metadata: response.video_metadata || metadata,
                },
              }
            : item
        )
      )

      // Invalidate subscription cache to force immediate quota refresh
      invalidateSubscriptionCache(accountId).catch(err =>
        console.warn('Failed to refresh subscription cache:', err)
      )

      // Clean up controller
      uploadControllers.current.delete(id)

      return response
    } catch (error) {
      // Handle errors
      let errorMessage = 'Upload failed'

      if (error.name === 'AbortError') {
        errorMessage = 'Upload cancelled'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, status: 'error', error: errorMessage, progress: 0 }
            : item
        )
      )

      // Clean up controller
      uploadControllers.current.delete(id)

      throw error
    }
  }, [])

  // Process uploads in batches of MAX_CONCURRENT_UPLOADS
  const processBatch = useCallback(async (batch, accountId) => {
    // Upload all files in this batch in parallel
    const uploadPromises = batch.map((item) =>
      uploadFile(item, accountId).catch((error) => {
        console.error(`Failed to upload ${item.file.name}:`, error)
        return null // Return null for failed uploads so Promise.allSettled continues
      })
    )

    // Wait for all uploads in this batch to complete
    await Promise.allSettled(uploadPromises)
  }, [uploadFile])

  // Add files to upload queue
  const uploadFiles = useCallback(async (files, accountId) => {
    const validFiles = []
    const errors = []

    // Basic file validation (extension and size)
    // Full validation happens after metadata extraction in uploadFile()
    for (const file of files) {
      const fileExtension = file.name
        .toLowerCase()
        .slice(file.name.lastIndexOf('.'))
      const fileSizeMB = file.size / (1024 * 1024)

      // Check extension
      if (!VIDEO_CONSTRAINTS.ALLOWED_EXTENSIONS.includes(fileExtension)) {
        errors.push(
          `File "${file.name}" is not a supported video format (MP4 or MOV only)`
        )
        continue
      }

      // Check file size
      if (fileSizeMB > VIDEO_CONSTRAINTS.MAX_SIZE_MB) {
        errors.push(
          `File "${file.name}" exceeds ${VIDEO_CONSTRAINTS.MAX_SIZE_MB}MB limit (${fileSizeMB.toFixed(2)}MB)`
        )
        continue
      }

      validFiles.push(file)
    }

    // Show validation errors
    if (errors.length > 0) {
      errors.forEach((error) => {
        console.error('File validation error:', error)
        // You could show a toast notification here
      })
    }

    // Add valid files to queue
    if (validFiles.length > 0) {
      const newQueueItems = validFiles.map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: createPreview(file),
        status: 'pending',
        progress: 0,
        error: null,
        metadata: null,
        mediaId: null,
        videoData: null,
      }))

      setUploadQueue((prev) => [...prev, ...newQueueItems])

      // Process uploads in batches for parallelization
      console.log(
        `Starting parallel video uploads: ${newQueueItems.length} files, max ${MAX_CONCURRENT_UPLOADS} concurrent`
      )

      for (let i = 0; i < newQueueItems.length; i += MAX_CONCURRENT_UPLOADS) {
        const batch = newQueueItems.slice(i, i + MAX_CONCURRENT_UPLOADS)
        console.log(
          `Processing batch ${Math.floor(i / MAX_CONCURRENT_UPLOADS) + 1}: ${batch.length} files`
        )
        await processBatch(batch, accountId)
      }

      console.log('All video uploads completed')
    }
  }, [processBatch])

  // Remove item from queue
  const removeFromQueue = useCallback((id) => {
    // Cancel upload if in progress
    const controller = uploadControllers.current.get(id)
    if (controller) {
      controller.abort()
      uploadControllers.current.delete(id)
    }

    // Remove from queue and revoke preview URL
    setUploadQueue((prev) => {
      const item = prev.find((item) => item.id === id)
      if (item?.preview) {
        URL.revokeObjectURL(item.preview)
      }
      return prev.filter((item) => item.id !== id)
    })
  }, [])

  // Retry failed upload
  const retryUpload = useCallback(
    async (id, accountId) => {
      const item = uploadQueue.find((item) => item.id === id)
      if (item && item.status === 'error') {
        await uploadFile(item, accountId)
      }
    },
    [uploadQueue, uploadFile]
  )

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    setUploadQueue((prev) => {
      // Revoke preview URLs for completed items
      prev
        .filter((item) => item.status === 'complete')
        .forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview)
          }
        })
      // Keep only non-completed items
      return prev.filter((item) => item.status !== 'complete')
    })
  }, [])

  // Cancel all uploads
  const cancelAllUploads = useCallback(() => {
    uploadControllers.current.forEach((controller) => controller.abort())
    uploadControllers.current.clear()

    setUploadQueue((prev) =>
      prev.map((item) => {
        if (
          item.status === 'extracting' ||
          item.status === 'uploading'
        ) {
          return { ...item, status: 'error', error: 'Upload cancelled', progress: 0 }
        }
        return item
      })
    )
  }, [])

  // Get upload statistics
  const getStats = useCallback(() => {
    const pending = uploadQueue.filter((item) => item.status === 'pending')
      .length
    const extracting = uploadQueue.filter((item) => item.status === 'extracting')
      .length
    const uploading = uploadQueue.filter((item) => item.status === 'uploading')
      .length
    const complete = uploadQueue.filter((item) => item.status === 'complete')
      .length
    const error = uploadQueue.filter((item) => item.status === 'error').length
    const total = uploadQueue.length

    return {
      pending,
      extracting,
      uploading,
      complete,
      error,
      total,
      inProgress: extracting + uploading,
      hasActive: extracting + uploading > 0,
      hasErrors: error > 0,
      hasCompleted: complete > 0,
    }
  }, [uploadQueue])

  return {
    uploadQueue,
    uploadFiles,
    removeFromQueue,
    retryUpload,
    clearCompleted,
    cancelAllUploads,
    getStats,
  }
}
